/**
 * Per-variable sanity checks for the SED Variable Browser.
 * Pure functions — no I/O.
 */

import type { SEDVariable } from '../types/sedAmendments';

export interface SEDVariableWarning {
  severity: 'warning' | 'error';
  message: string;
}

const DIST_KM_SUFFIXES = ['DistKm', 'distKm', 'distanceKm'];

function isDistanceField(path: string): boolean {
  const leaf = path.split('.').pop() ?? '';
  return DIST_KM_SUFFIXES.some(s => leaf.endsWith(s) || leaf.includes('Dist'));
}

/**
 * Returns engineering sanity warnings for a single extracted variable.
 * Cross-variable checks (e.g. peak vs average temperature) are omitted
 * to keep the helper per-row and side-effect free.
 */
export function getSEDVariableWarnings(v: SEDVariable): SEDVariableWarning[] {
  const warnings: SEDVariableWarning[] = [];
  const val = v.value;

  if (typeof val !== 'number' || !Number.isFinite(val)) {
    return warnings;
  }

  if (val < 0 && isDistanceField(v.path)) {
    warnings.push({ severity: 'error', message: 'Distance cannot be negative' });
  }

  if (v.path.endsWith('nearestIxDistKm') && val > 500) {
    warnings.push({
      severity: 'warning',
      message: 'Distance to IX exceeds 500 km — verify extraction',
    });
  }

  if (v.path.endsWith('substationDistKm') && val > 100) {
    warnings.push({
      severity: 'warning',
      message: 'Substation distance exceeds 100 km — verify extraction',
    });
  }

  if (v.path.endsWith('nearestReactorDistKm') && val > 0 && val < 1) {
    warnings.push({
      severity: 'warning',
      message: 'Reactor distance under 1 km — verify extraction',
    });
  }

  if ((v.path.endsWith('peakTemp') || v.path.endsWith('peakWetBulb')) && val < -60) {
    warnings.push({ severity: 'warning', message: 'Peak temperature unusually low' });
  }

  if ((v.path.endsWith('peakTemp') || v.path.endsWith('avgTemp')) && val > 55) {
    warnings.push({ severity: 'warning', message: 'Temperature exceeds 55 °C — verify extraction' });
  }

  if (v.path.endsWith('pga') && (val < 0 || val > 3)) {
    warnings.push({ severity: 'warning', message: 'PGA outside typical range (0–3 g)' });
  }

  if (v.path.endsWith('slopePercent') && val > 45) {
    warnings.push({ severity: 'warning', message: 'Terrain slope exceeds 45% — verify extraction' });
  }

  return warnings;
}
